import { useEffect, useState } from "react";

import type { RegisteredComponent } from "@builder.io/sdk-react";
import { fetchOneEntry } from "@builder.io/sdk-react";

import { useBuilder } from "~/hec/hooks/useBuilder";

import { ModelMap } from "../../../model-map";
import { TpaLearningCenterConsentModal } from "./consent-modal";

interface BuilderTpaLearningCenterConsentProps {
  /** Inline override (Builder editor input); when absent we fetch the data model. */
  modalContent?: string;
  className?: string;
  attributes?: Record<string, unknown>;
}

/**
 * Builder block for the TPA Learning Center consent modal (SFCC-11508).
 *
 * Port of hec-composable
 * `hec/builder/blocks/tpa-learning-center-consent/tpaLearningCenterConsent.jsx`.
 * The modal copy lives in the dedicated `tpa-learning-center-consent` data
 * model (one `default` entry per space), so when no inline `modalContent` is
 * supplied we client-fetch it via `fetchOneEntry` (bare specifier, per the
 * Builder request-pattern rule). Optional content — render nothing on a
 * fetch miss/error rather than an empty shell.
 *
 * Registered in `customComponents` (the alias was mapped but unregistered, so
 * it previously rendered empty).
 */
function BuilderTpaLearningCenterConsent({
  modalContent,
  className,
  attributes,
}: BuilderTpaLearningCenterConsentProps) {
  const apiKey = useBuilder().config.api_key;
  const [fetched, setFetched] = useState<string | undefined>(undefined);

  useEffect(() => {
    // Inline content provided (editor) — skip the fetch.
    if (modalContent) return;
    if (!apiKey) return;
    let cancelled = false;
    void (async () => {
      try {
        const entry = await fetchOneEntry({
          model: ModelMap.tpaLearningCenterConsent,
          apiKey,
          // No real targeting on this model — use the scheduling sentinel so
          // Builder still evaluates Targeting/Scheduling.
          userAttributes: { useScheduling: true },
          fetchOptions: { signal: AbortSignal.timeout(2500) },
        });
        if (cancelled) return;
        const content = (entry?.data as { modalContent?: string } | undefined)
          ?.modalContent;
        setFetched(content ?? undefined);
      } catch {
        // Optional marketing content — degrade silently.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiKey, modalContent]);

  const content = modalContent ?? fetched;
  if (!content) return null;

  return (
    <TpaLearningCenterConsentModal
      modalContent={content}
      className={className}
      attributes={attributes}
    />
  );
}

export const TpaLearningCenterConsentDefinition = {
  component: BuilderTpaLearningCenterConsent,
  name: "TpaLearningCenterConsent",
  noWrap: true,
  inputs: [
    {
      name: "modalContent",
      type: "html",
      required: false,
      helperText:
        "Optional inline HTML override. Leave empty to use the tpa-learning-center-consent model's published entry.",
    },
  ],
} satisfies RegisteredComponent;

export default BuilderTpaLearningCenterConsent;
